'use client';

export {};

declare global {
  interface Window {
    tgp: (action: string, id: string) => void;
  }
}

import { useMutation } from '@apollo/client/react';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { miniApp } from '@tma.js/sdk-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { SEND_KEYBOARD } from '@/.graphql';
import ShotikIcon from '@/public/icons/shotik.svg';
import { Header } from '@/src/components/Header';
import {
  useProfileContext,
  useGTMContext,
  useMobileContext,
} from '@/src/providers';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  wrapper: 'mt-6 pt-16 px-5',
  banner:
    'flex flex-col gap-2 justify-center items-center p-4 bg-fill-quinary text-center rounded-lg mb-10',
  balance: 'flex items-center gap-2 bg-fill-menu rounded-full px-3 py-2',
  label: 'font-bold text-base',
  icon: 'w-5 h-5',
  image: 'm-auto w-20 h-26.5',
  info: 'py-5 mb-6 flex flex-col gap-1 justify-center text-center',
  title: 'font-semibold text-xl',
  description: 'text-sm text-label-secondary',
  button: 'bg-fill-button rounded-xl w-full py-5',
  buttonText: 'text-label-black font-extrabold text-base',
};

export const TopUpBalancePortal: FC = () => {
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const t = useTranslations('balanceModal');
  const { closePortal } = usePortalContext();
  const { isMobiles } = useMobileContext();

  const [sendKeyboard, { loading }] = useMutation(SEND_KEYBOARD);

  const onCloseWebApp = async () => {
    if (typeof window !== 'undefined' && typeof window.tgp === 'function') {
      window.tgp('event', '7T4zQojL-DDTj064F');
    }

    sendGTM({
      event: 'Pay_popap',
    });

    try {
      await sendKeyboard({
        variables: {
          keyboard: 'generationMenu',
        },
      });
      if (miniApp.close.isAvailable()) {
        miniApp.close();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <PortalModal
      portalId={PORTALS.TopUpBalance}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={closePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.banner}>
              <div className={styles.title}>{t('yourBalance')}</div>
              <div className={styles.balance}>
                <ShotikIcon className={styles.icon} />
                <div className={styles.label}>
                  {profile?.imageGenerationsLeft}
                </div>
              </div>
            </div>

            <Image
              width={80}
              height={106}
              src="/images/banner-img.webp"
              alt="banner"
              className={styles.image}
            />

            <div className={styles.info}>
              <div className={styles.title}>{t('title')} </div>
              <div className={styles.description}>
                {t('description', {
                  brand: profile?.brandName,
                })}
              </div>
            </div>

            <button
              className={styles.button}
              onClick={onCloseWebApp}
              disabled={loading}
            >
              <div className={styles.buttonText}>{t('button')}</div>
            </button>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
